import MetricDisplay from '@/components/ui/MetricDisplay';
import { PATHS } from '@/constants/Paths';
import { useStatsCount } from '@/hooks/useStats';

import { useCallback } from 'react';
import { useNavigate } from 'react-router';
import {
  ConfirmedReservationsTable,
  ReservationRequestsTable,
} from '@/components/dashboard/ReservationsTables';
import { useTranslation } from '@/i18n';
import { useDistributorLabels } from '@/hooks/useDistributorLabels';

const DashboardPage = () => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-6 p-6 items-start grow bg-brand-25 h-full">
      <h1 className="text-[#0c111d] text-[32px] font-semibold font-['Geist'] leading-loose">
        {t('common.dashboard')}
      </h1>
      <CountMetrics />
      <div className="flex flex-1 flex-col md:flex-row gap-6 self-stretch">
        <div className="flex flex-1 flex-col gap-6">
          <MetricDisplay
            title={t('pages.dashboard.confirmedReservations')}
            subText={t('pages.dashboard.confirmedReservationsDescription')}
            childPosition="bottom"
          >
            <ConfirmedReservationsTable />
          </MetricDisplay>
          <MetricDisplay
            title={t('pages.dashboard.reservationRequests')}
            subText={t('pages.dashboard.reservationRequestsDescription')}
            childPosition="bottom"
          >
            <ReservationRequestsTable />
          </MetricDisplay>
        </div>
        <img
          src="/images/dashboard-illustration.jpg"
          className="sticky shrink-0 h-full top-20 hidden 2xl:block"
        />
      </div>
    </div>
  );
};

const CountMetrics = () => {
  return (
    <div className="grid grid-cols-1 [@media_(min-width:500px)]:grid-cols-2 [@media_(min-width:910px)]:grid-cols-4 gap-6 w-full">
      <SingleMetrics type="suppliers" />
      <SingleMetrics type="services" />
      <SingleMetrics type="circuits" />
      <SingleMetrics type="distributors" />
      <SingleMetrics type="reservations" />
    </div>
  );
};

export type MetricsEnum =
  | 'circuits'
  | 'services'
  | 'suppliers'
  | 'distributors'
  | 'reservations'
  | 'receivers'
  | 'shared-circuits'
  | 'estimates'
  | 'clients';

const METRICS_TITLE: {
  [K in MetricsEnum]: string;
} = {
  circuits: 'Circuits',
  services: 'Prestations',
  suppliers: 'Fournisseurs',
  distributors: 'Distributeurs',
  reservations: 'Réservations',
  receivers: 'Fournisseurs',
  'shared-circuits': 'Circuits disponibles',
  estimates: 'Devis réalisés',
  clients: 'Clients',
};

type MetricsProps = {
  type: MetricsEnum;
};

export const SingleMetrics = ({ type }: MetricsProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isTourOperator } = useDistributorLabels();

  // Use translation keys instead of hard-coded strings
  const getMetricTitle = (metricType: MetricsEnum): string => {
    switch (metricType) {
      case 'circuits':
        return t('pages.dashboard.metrics.circuits');
      case 'services':
        return t('pages.dashboard.metrics.services');
      case 'suppliers':
        return t('pages.dashboard.metrics.suppliers');
      case 'distributors':
        return t('pages.dashboard.metrics.distributors');
      case 'reservations':
        return t('pages.dashboard.metrics.reservations');
      case 'receivers':
        return t('pages.dashboard.metrics.receivers');
      case 'shared-circuits':
        return t('pages.dashboard.metrics.sharedCircuits');
      case 'estimates':
        return t('pages.dashboard.metrics.estimates');
      case 'clients':
        return isTourOperator
          ? t('pages.dashboard.metrics.agencies')
          : t('pages.dashboard.metrics.clients');
      default:
        return METRICS_TITLE[type]; // Fallback to original
    }
  };

  const title = getMetricTitle(type);
  const { data, isLoading } = useStatsCount(type);

  const navigateToList = useCallback(() => {
    switch (type) {
      case 'suppliers':
        navigate(PATHS.home.suppliers.list);
        break;
      case 'services':
        navigate(PATHS.home.benefits.list);
        break;
      case 'circuits':
        navigate(PATHS.home.circuits.list);
        break;
      case 'distributors':
        navigate(PATHS.home.distributors.list);
        break;
      case 'reservations':
        navigate(PATHS.home.reservations.requests.list);
        break;
      case 'receivers':
        navigate(PATHS.distributors.receivers.list);
        break;
      case 'shared-circuits':
        navigate(PATHS.distributors.circuits.list);
        break;
      case 'estimates':
        navigate(PATHS.distributors.estimates.list);
        break;
      case 'clients':
        navigate(PATHS.distributors.clients.list);
        break;
    }
  }, [navigate, type]);

  return (
    <MetricDisplay
      subTitle={title}
      value={data}
      isValueLoading={isLoading}
      navigateToList={navigateToList}
    />
  );
};

export default DashboardPage;
